/**
 * Ask Titan Store
 *
 * Holds the conversation for the always-available "Ask Titan" chat panel. Sends
 * a user message to the intelligence query agent (POST /api/intelligence/agent)
 * and appends the assistant reply along with the tools the agent used. All API
 * calls go through the centralized api.ts client — no direct fetch() here.
 */

import { create } from 'zustand';
import { agentApi, ApiError } from '../lib/api';

export type AskTitanRole = 'user' | 'assistant';

export interface AskTitanMessage {
  id: string;
  role: AskTitanRole;
  content: string;
  timestamp: number;
  toolsUsed?: string[];
}

interface AskTitanState {
  messages: AskTitanMessage[];
  loading: boolean;
  error: string | null;
  /** Name of the tool the agent is currently calling (transient, for "using {tool}…"). */
  activeTool: string | null;
  _inFlight: boolean;

  sendMessage: (text: string) => Promise<void>;
  clearMessages: () => void;
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const useAskTitanStore = create<AskTitanState>((set, get) => ({
  messages: [],
  loading: false,
  error: null,
  activeTool: null,
  _inFlight: false,

  sendMessage: async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Skip if a request is already in flight — never stack sends.
    if (get()._inFlight) return;

    // Prior turns become conversation history (captured BEFORE the optimistic
    // append) so follow-ups like "and last week?" have context.
    const history = get().messages.map((m) => ({ role: m.role, content: m.content }));

    const userMessage: AskTitanMessage = {
      id: makeId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };
    // Empty assistant message we stream tokens into.
    const assistantId = makeId();
    const assistantMessage: AskTitanMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    };
    // Optimistically append the user message AND the empty assistant message.
    set((state) => ({
      messages: [...state.messages, userMessage, assistantMessage],
      _inFlight: true,
      loading: true,
      error: null,
      activeTool: null,
    }));

    // Immutably mutate the streaming assistant message (matched by id).
    const updateAssistant = (mutate: (m: AskTitanMessage) => AskTitanMessage) =>
      set((state) => ({
        messages: state.messages.map((m) => (m.id === assistantId ? mutate(m) : m)),
      }));

    let streamErrored = false;

    try {
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        void agentApi.askStream(trimmed, history, {
          onTool: (name) => {
            set({ activeTool: name });
            updateAssistant((m) => ({
              ...m,
              toolsUsed: [...(m.toolsUsed ?? []), name],
            }));
          },
          onDelta: (delta) => {
            updateAssistant((m) => ({ ...m, content: m.content + delta }));
          },
          onDone: (toolsUsed) => {
            updateAssistant((m) => ({
              ...m,
              // Prefer the authoritative tools_used from `done`; fall back to streamed.
              toolsUsed: toolsUsed.length > 0 ? toolsUsed : m.toolsUsed,
            }));
            set({ loading: false, error: null, activeTool: null });
            finish();
          },
          onError: (msg) => {
            streamErrored = true;
            set({ error: msg });
            finish();
          },
        });
      });
    } catch (e) {
      // Any thrown exception from the stream pathway → fall back below.
      streamErrored = true;
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      set({ error: msg });
    }

    // Fallback: if streaming failed, fetch a complete answer from the non-stream
    // endpoint and fill the assistant message so the user always gets a reply.
    if (streamErrored) {
      try {
        const res = await agentApi.ask(trimmed, history);
        updateAssistant((m) => ({
          ...m,
          content: res.reply,
          toolsUsed: res.tools_used,
        }));
        set({ loading: false, error: null, activeTool: null });
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
        set({ error: msg, loading: false, activeTool: null });
      }
    }

    set({ _inFlight: false });
  },

  clearMessages: () => set({ messages: [], error: null, activeTool: null }),
}));
